import React, { useContext } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Combobox } from '@/components/ui/combobox';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import { useCrLocations } from '@/hooks/useCrLocations';
import { type CustomerInfo } from '@/types';

interface CustomerAddressFieldsProps {
  value: CustomerInfo['address'];
  onChange: (patch: Partial<CustomerInfo['address']>) => void;
  error?: string | null;
}

const CustomerAddressFields: React.FC<CustomerAddressFieldsProps> = ({
  value,
  onChange,
  error,
}) => {
  const { translator } = useContext(LanguageProviderContext);
  const { provinces, cantons, districts, isLoading } = useCrLocations(
    value.province,
    value.canton
  );

  const searchPlaceholder = translator('Invoices.customerAddressSearchPlaceholder');
  const emptyMessage = translator('Invoices.customerAddressEmpty');
  const loadingMessage = translator('Global.loading');

  return (
    <div className="space-y-4 md:pl-6 md:border-l md:border-border">
      <div className="grid gap-2">
        <Label>{translator('Invoices.customerProvinceLabel')} *</Label>
        <Combobox
          options={provinces}
          value={value.province || undefined}
          onValueChange={(province) => onChange({ province, canton: '', district: '' })}
          placeholder={translator('Invoices.customerProvincePlaceholder')}
          searchPlaceholder={searchPlaceholder}
          emptyMessage={emptyMessage}
          loading={isLoading}
          loadingMessage={loadingMessage}
        />
      </div>
      <div className="grid gap-2">
        <Label>{translator('Invoices.customerCantonLabel')} *</Label>
        <Combobox
          options={cantons}
          value={value.canton || undefined}
          onValueChange={(canton) => onChange({ canton, district: '' })}
          placeholder={translator('Invoices.customerCantonPlaceholder')}
          searchPlaceholder={searchPlaceholder}
          emptyMessage={emptyMessage}
          loading={isLoading}
          loadingMessage={loadingMessage}
          disabled={!value.province}
        />
      </div>
      <div className="grid gap-2">
        <Label>{translator('Invoices.customerDistrictLabel')} *</Label>
        <Combobox
          options={districts}
          value={value.district || undefined}
          onValueChange={(district) => onChange({ district })}
          placeholder={translator('Invoices.customerDistrictPlaceholder')}
          searchPlaceholder={searchPlaceholder}
          emptyMessage={emptyMessage}
          loading={isLoading}
          loadingMessage={loadingMessage}
          disabled={!value.canton}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="customer-address-other">
          {translator('Invoices.customerOtherAddressLabel')}
        </Label>
        <Input
          id="customer-address-other"
          value={value.other}
          onChange={(event) => onChange({ other: event.target.value })}
        />
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
};

export default CustomerAddressFields;
